/* De Vergulde Kaars: product page helpers (sticky add-to-cart bar on mobile). */
if (!customElements.get('dvk-sticky-atc')) {
  customElements.define(
    'dvk-sticky-atc',
    class DvkStickyAtc extends HTMLElement {
      connectedCallback() {
        if (this.closest('quick-add-modal, .quick-add-modal') || !('IntersectionObserver' in window)) {
          this.remove();
          return;
        }
        this.target = document.getElementById(this.dataset.target);
        if (!this.target) return;

        this.mq = window.matchMedia('(max-width: 749px)');
        this.visible = false;
        this.pastButton = false;

        this.intersecting = true;
        this.observer = new IntersectionObserver(
          ([entry]) => {
            this.intersecting = entry.isIntersecting;
            this.measure();
          },
          { threshold: 0 }
        );
        this.observer.observe(this.target);

        // Jumps (anchor links, restored scroll) can skip from "above" to "below" without an
        // intersection change, so re-measure on scroll while the button is out of view.
        this.ticking = false;
        this.onScroll = () => {
          if (this.intersecting || this.ticking) return;
          this.ticking = true;
          requestAnimationFrame(() => {
            this.ticking = false;
            this.measure();
          });
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });

        // Body padding follows the real bar height (it grows when the button wraps at large text).
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => {
            document.documentElement.style.setProperty('--dvk-sticky-h', `${this.offsetHeight}px`);
          });
          this.resizeObserver.observe(this);
        }

        this.onMq = () => this.update();
        this.mq.addEventListener?.('change', this.onMq);

        // The bar submits the main form, so it shows the stepper's quantity when that is above 1.
        this.quantityId = `Quantity-${this.dataset.section}`;
        this.onQuantity = (event) => {
          if (event.target && event.target.id === this.quantityId) this.syncQuantity();
        };
        document.addEventListener('change', this.onQuantity);
        document.addEventListener('input', this.onQuantity);
        this.syncQuantity();

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event));
        }
      }

      syncQuantity() {
        const input = document.getElementById(this.quantityId);
        const qtyEl = this.querySelector('[data-sticky-qty]');
        if (!qtyEl) return;
        const qty = input ? parseInt(input.value, 10) : 1;
        if (qty > 1) {
          qtyEl.textContent = `${qty} \u00d7 `;
          qtyEl.hidden = false;
        } else {
          qtyEl.textContent = '';
          qtyEl.hidden = true;
        }
      }

      measure() {
        // Only show once the main button has scrolled *above* the viewport.
        this.pastButton = !this.intersecting && this.target.getBoundingClientRect().bottom < 0;
        this.update();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.resizeObserver?.disconnect();
        document.documentElement.style.removeProperty('--dvk-sticky-h');
        if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
        this.mq?.removeEventListener?.('change', this.onMq);
        if (this.onQuantity) {
          document.removeEventListener('change', this.onQuantity);
          document.removeEventListener('input', this.onQuantity);
        }
        this.unsubscribe?.();
        document.documentElement.classList.remove('dvk-sticky-atc-open');
      }

      update() {
        const show = this.mq.matches && this.pastButton && !this.hasAttribute('data-unavailable');
        if (show === this.visible) return;
        this.visible = show;
        this.classList.toggle('is-visible', show);
        this.toggleAttribute('inert', !show);
        this.setAttribute('aria-hidden', String(!show));
        document.documentElement.classList.toggle('dvk-sticky-atc-open', show);
      }

      onVariantChange({ data }) {
        if (!data || data.sectionId !== this.dataset.section) return;
        const variant = data.variant;
        const button = this.querySelector('button');
        const available = Boolean(variant && variant.available);
        this.toggleAttribute('data-unavailable', !available);
        if (button) button.disabled = !available;
        const priceRoot = data.html?.querySelector(`#price-${data.sectionId} .price`);
        const onSale = priceRoot?.classList.contains('price--on-sale');
        const newPrice = priceRoot?.querySelector(
          onSale ? '.price__sale .price-item--last' : '.price__regular .price-item--regular'
        );
        const priceEl = this.querySelector('[data-sticky-price]');
        const amountEl = this.querySelector('[data-sticky-amount]');
        if (newPrice && priceEl && amountEl) {
          amountEl.textContent = newPrice.textContent.trim();
          priceEl.classList.toggle('dvk-sticky-atc__price--sale', Boolean(onSale));
        }
        // The quantity block is re-rendered on variant change; read it again afterwards.
        requestAnimationFrame(() => this.syncQuantity());
        this.update();
      }
    }
  );
}

/*
 * Cart API errors come back in the shop's language (Dutch now, English before). Swap them for the Dutch
 * strings that buy-buttons.liquid puts on the error wrapper (data-dvk-msg-*).
 */
(function () {
  if (window.dvkCartErrorsTranslated) return;
  window.dvkCartErrorsTranslated = true;

  const translate = (wrapper) => {
    const target = wrapper.querySelector('.product-form__error-message');
    if (!target || !wrapper.dataset.dvkMsgGeneric) return;
    const text = target.textContent.trim();
    if (!text) return;
    const d = wrapper.dataset;
    const known = [d.dvkMsgMax, d.dvkMsgSoldOut, d.dvkMsgGeneric];
    if (known.includes(text) || text === target.dataset.dvkTranslated) return;

    let next = d.dvkMsgGeneric;
    // English patterns, plus the Dutch wording Shopify uses now that the store language is Dutch.
    const partial =
      text.match(/only (\d+) items? (?:was|were) added/i) || text.match(/slechts (\d+) artikel(?:en)? .*toegevoegd/i);
    const limit = text.match(/only add (\d+)/i) || text.match(/^all (\d+)/i) || text.match(/slechts (\d+)/i);
    if (partial && d.dvkMsgPartial) next = d.dvkMsgPartial.replace('__N__', partial[1]);
    else if (/maximum quantity|maximale hoeveelheid|can(?:'|’)t add more|^all \d+/i.test(text)) next = d.dvkMsgMax;
    else if (limit && d.dvkMsgLimit) next = d.dvkMsgLimit.replace('__N__', limit[1]);
    else if (/sold out|uitverkocht/i.test(text)) next = d.dvkMsgSoldOut;

    target.dataset.dvkTranslated = next;
    target.textContent = next;
  };

  const start = () => {
    document.querySelectorAll('.product-form__error-message-wrapper').forEach(translate);
    new MutationObserver((mutations) => {
      for (const m of mutations) {
        const el = m.target.nodeType === 1 ? m.target : m.target.parentElement;
        const wrapper = el?.closest?.('.product-form__error-message-wrapper');
        if (wrapper) translate(wrapper);
      }
    }).observe(document.body, { childList: true, characterData: true, subtree: true });
  };

  if (document.body) start();
  else document.addEventListener('DOMContentLoaded', start);
})();
